import React, { useEffect, useState } from "react";
import {
    MapContainer,
    TileLayer,
    Marker,
    Popup,
    ZoomControl,
    useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import axios from "axios";
import L from "leaflet";
import { baseurladdress } from "../baseurl";

/* ---------------- BLINKING RED ICON ---------------- */
const blinkingRedIcon = L.divIcon({
    className: "blinking-marker",
    html: `<div></div>`,
    iconSize: [20, 20],
});

/* ---------------- AUTO FOCUS MAP ---------------- */
const FitCurrent = ({ point }) => {
    const map = useMap();
    useEffect(() => {
        if (point) {
            map.setView(point, 16);
        }
    }, [point, map]);
    return null;
};

const AdminLiveTracking = () => {
    const [staffLocations, setStaffLocations] = useState([]);
    const [selectedStaffId, setSelectedStaffId] = useState(null);
    const [currentPoint, setCurrentPoint] = useState(null);

    const accesstoken = localStorage.getItem("token");
    const roleId = Number(localStorage.getItem("roleid"));
    const isFieldStaff = roleId === 5;

    const panelHeading =
        roleId === 5 ? "📍 My Live Location" :
        roleId === 3 ? "📍 Live Tracking — My Region" :
        roleId === 4 ? "📍 Live Tracking — My Area(s)" :
                      "📍 Live Staff Tracking";

    useEffect(() => {
        const fetchStaffLocations = () => {
            axios
                .get(baseurladdress + "livetrack/getstafflocations", {
                    headers: { Authorization: `Bearer ${accesstoken}` },
                })
                .then((res) => {
                    if (res.data.status === "Success") {
                        const formattedStaff = (res.data.data || [])
                            .map((row) => {
                                const latValue = row.latitude ?? row.lat ?? row.Latitude;
                                const lngValue = row.longitude ?? row.lng ?? row.Longitude;

                                const latitude = latValue === null || latValue === undefined || latValue === ""
                                    ? null
                                    : Number(String(latValue).trim());
                                const longitude = lngValue === null || lngValue === undefined || lngValue === ""
                                    ? null
                                    : Number(String(lngValue).trim());

                                if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
                                    return null;
                                }

                                return {
                                    staff_id: row.staff_id ?? row.staffId ?? row.id,
                                    staff_name: row.staff_name ?? row.staffName ?? row.name ?? "Staff",
                                    latitude,
                                    longitude,
                                };
                            })
                            .filter(Boolean);

                        setStaffLocations(formattedStaff);

                        if (formattedStaff.length > 0) {
                            const currentSelectionStillExists = formattedStaff.some(
                                (staff) => String(staff.staff_id) === String(selectedStaffId)
                            );

                            if (!selectedStaffId || !currentSelectionStillExists) {
                                const firstStaff = formattedStaff[0];
                                setSelectedStaffId(firstStaff.staff_id);
                                setCurrentPoint([firstStaff.latitude, firstStaff.longitude]);
                            } else {
                                const selected = formattedStaff.find(
                                    (staff) => String(staff.staff_id) === String(selectedStaffId)
                                );
                                if (selected) {
                                    setCurrentPoint([selected.latitude, selected.longitude]);
                                }
                            }
                        } else {
                            setSelectedStaffId(null);
                            setCurrentPoint(null);
                        }
                    }
                })
                .catch(console.error);
        };

        fetchStaffLocations();
        const i = setInterval(fetchStaffLocations, 30000);
        return () => clearInterval(i);
    }, [accesstoken]);

    const selectedStaff = staffLocations.find(
        (staff) => String(staff.staff_id) === String(selectedStaffId)
    );
    const selectedStaffName = selectedStaff?.staff_name || "Staff";

    const containerStyle = {
        padding: "clamp(10px, 3vw, 20px)",
        maxWidth: "1600px",
        margin: "0 auto",
    };

    const headingStyle = {
        marginBottom: "15px",
        fontSize: "clamp(1.2rem, 3vw, 1.5rem)",
        color: "#333",
        fontWeight: "600",
    };

    const gridStyle = {
        display: "grid",
        gridTemplateColumns: "1fr",
        gap: "15px",
        minHeight: "clamp(400px, 70vh, 800px)",
        background: "#fff",
        borderRadius: "clamp(8px, 2vw, 12px)",
        boxShadow: "0 6px 20px rgba(0,0,0,0.1)",
        padding: "clamp(10px, 2vw, 15px)",
    };

    const leftPanelStyle = {
        border: "1px solid #eee",
        borderRadius: "clamp(6px, 1.5vw, 10px)",
        padding: "clamp(12px, 2vw, 15px)",
        background: "#fafafa",
    };

    const selectStyle = {
        width: "100%",
        marginTop: "10px",
        padding: "10px 12px",
        fontSize: "16px",
        borderRadius: "6px",
        border: "1px solid #ccc",
        backgroundColor: "#fff",
        cursor: "pointer",
        transition: "border-color 0.3s",
    };

    const mapContainerStyle = {
        borderRadius: "clamp(6px, 1.5vw, 10px)",
        overflow: "hidden",
        border: "1px solid #eee",
        height: "clamp(350px, 50vh, 100%)",
    };

    return (
        <div style={containerStyle}>
            <h4 style={headingStyle}>{panelHeading}</h4>

            <div style={gridStyle} className="tracking-grid">
                <div style={leftPanelStyle}>
                    <strong style={{ fontSize: "clamp(0.9rem, 2vw, 1rem)", color: "#333" }}>
                        🟢 Staff with current location
                    </strong>

                    {isFieldStaff ? (
                        <div style={{
                            marginTop: "10px",
                            padding: "10px 12px",
                            borderRadius: "6px",
                            border: "1px solid #c8e6c9",
                            background: "#f1f8e9",
                            fontSize: "0.9rem",
                            color: "#2e7d32",
                            fontWeight: "600",
                        }}>
                            {selectedStaffName || "You are not currently active"}
                        </div>
                    ) : (
                        <select
                            style={selectStyle}
                            value={selectedStaffId || ""}
                            onChange={(e) => {
                                const staffId = e.target.value || null;
                                setSelectedStaffId(staffId);

                                const selected = staffLocations.find(
                                    (staff) => String(staff.staff_id) === String(staffId)
                                );

                                if (selected) {
                                    setCurrentPoint([selected.latitude, selected.longitude]);
                                } else {
                                    setCurrentPoint(null);
                                }
                            }}
                        >
                            <option value="">-- Select Staff --</option>
                            {staffLocations.map((staff) => (
                                <option key={staff.staff_id} value={String(staff.staff_id)}>
                                    {staff.staff_name}
                                </option>
                            ))}
                        </select>
                    )}

                    <div style={{
                        fontSize: "clamp(0.75rem, 1.8vw, 0.85rem)",
                        marginTop: "10px",
                        color: "#555",
                        fontWeight: "500",
                    }}>
                        {staffLocations.length} staff with location data
                    </div>

                    {staffLocations.length > 0 ? (
                        <div style={{ marginTop: "12px", display: "grid", gap: "8px" }}>
                            {staffLocations.map((staff) => (
                                <div
                                    key={staff.staff_id}
                                    style={{
                                        padding: "8px 10px",
                                        borderRadius: "6px",
                                        border: String(selectedStaffId) === String(staff.staff_id) ? "1px solid #4caf50" : "1px solid #e0e0e0",
                                        background: String(selectedStaffId) === String(staff.staff_id) ? "#f1f8e9" : "#fff",
                                        fontSize: "0.85rem",
                                        color: "#333",
                                    }}
                                >
                                    <strong>{staff.staff_name}</strong>
                                    <div style={{ color: "#666", marginTop: "2px" }}>
                                        Lat: {staff.latitude.toFixed(6)} / Lng: {staff.longitude.toFixed(6)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div style={{
                            marginTop: "12px",
                            padding: "10px",
                            borderRadius: "6px",
                            background: "#fff3e0",
                            color: "#e65100",
                            fontSize: "0.82rem",
                        }}>
                            No staff location data is available right now. The rest may have ended their session or may not have worked today.
                        </div>
                    )}

                    <div style={{
                        marginTop: "10px",
                        fontSize: "0.78rem",
                        color: "#777",
                        lineHeight: "1.5",
                    }}>
                        Staff not shown here may have ended their session or may not have worked today.
                    </div>
                </div>

                <div style={mapContainerStyle}>
                    <MapContainer
                        center={[10.0, 76.0]}
                        zoom={7}
                        style={{ height: "100%", width: "100%" }}
                        zoomControl={false}
                    >
                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

                        {currentPoint && (
                            <>
                                <Marker
                                    position={[Number(currentPoint[0]), Number(currentPoint[1])]} 
                                    icon={blinkingRedIcon}
                                >
                                    <Popup>
                                        <div style={{ fontSize: "0.9rem" }}>
                                            <strong>
                                                {isFieldStaff ? "My Location" : `Live: ${selectedStaffName}`}
                                            </strong>
                                            <br />
                                            Latitude: {Number(currentPoint[0]).toFixed(6)}
                                            <br />
                                            Longitude: {Number(currentPoint[1]).toFixed(6)}
                                        </div>
                                    </Popup>
                                </Marker>
                                <FitCurrent point={[Number(currentPoint[0]), Number(currentPoint[1])]} />
                            </>
                        )}

                        <ZoomControl position="topright" />
                    </MapContainer>
                </div>
            </div>

            <style>
                {`
                .blinking-marker div {
                    width: 18px;
                    height: 18px;
                    background: red;
                    border-radius: 50%;
                    box-shadow: 0 0 10px red;
                    animation: blink 1s infinite;
                }

                @keyframes blink {
                    0% { transform: scale(0.8); opacity: 1; }
                    50% { transform: scale(1.4); opacity: 0.4; }
                    100% { transform: scale(0.8); opacity: 1; }
                }

                @media (min-width: 768px) {
                    .tracking-grid {
                        grid-template-columns: 260px 1fr !important;
                        min-height: 70vh !important;
                    }
                }

                @media (min-width: 1200px) {
                    .tracking-grid {
                        grid-template-columns: 300px 1fr !important;
                    }
                }

                select:focus {
                    outline: none;
                    border-color: #4CAF50 !important;
                    box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1);
                }

                @media (max-width: 768px) {
                    .leaflet-control-container .leaflet-top.leaflet-right {
                        top: 10px;
                        right: 10px;
                    }
                    .leaflet-control-zoom { border-radius: 6px; }
                    .leaflet-container { touch-action: pan-x pan-y; }
                }

                .leaflet-popup-content {
                    font-size: clamp(0.8rem, 2vw, 0.9rem);
                }
                `}
            </style>
        </div>
    );
};

export default AdminLiveTracking;